// DEPRECATED COMPONENT, DO NOT USE. USE APP-COMPONENTS Calendar INSTEAD.
import React, { useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import { chunk, cloneDeep, concat, noop, some } from 'lodash';
export var getDays = function getDays(year, month) {
  var firstDay = moment({
    year: year,
    month: month
  }).startOf('month').startOf('week');
  var lastDay = moment({
    year: year,
    month: month
  }).endOf('month').endOf('week');
  var days = [];
  var day = firstDay;
  do {
    days = concat(days, cloneDeep(day));
    day = day.add(1, 'day');
  } while (day.isBefore(lastDay));
  return chunk(days, 7);
};
export var isDayInArray = function isDayInArray(day, daysArray) {
  var isUtc = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  return some(daysArray, function (selected) {
    if (isUtc) {
      return day.utc().isSame(moment(selected), 'day');
    }
    return day.isSame(moment(selected), 'day');
  });
};
export var isDaySelected = function isDaySelected(day, selectedDays, dateRange, isUtc) {
  if (dateRange.start || dateRange.end) {
    return isDayInArray(day, [dateRange.start, dateRange.end], isUtc);
  }
  return isDayInArray(day, selectedDays, isUtc);
};
export var isDayDisabled = function isDayDisabled(day, disabledDays, shouldDisableDay) {
  return isDayInArray(day, disabledDays) || shouldDisableDay(day);
};
export var isDayInRange = function isDayInRange(day, dateRange) {
  if (dateRange.start && dateRange.end) {
    return day.isBetween(moment(dateRange.start), moment(dateRange.end), 'day');
  }
  return false;
};
export var isDayOutOfMonth = function isDayOutOfMonth(day, month) {
  return day.month() !== month;
};
var UUCalendar = function UUCalendar(props) {
  var weekDays = props.weekDays,
    year = props.year,
    month = props.month,
    selectedDays = props.selectedDays,
    onDayClick = props.onDayClick,
    disabledDays = props.disabledDays,
    className = props.className,
    id = props.id,
    shouldDisableDay = props.shouldDisableDay,
    dateRange = props.dateRange,
    isUtc = props.isUtc;
  var days = getDays(year, month);
  var calendarRef = useRef(null);
  var calendarClass = classNames({
    'border-separate text-text-blue text-center select-none text-sm': true
  }, className);
  var dayClass = function dayClass(day) {
    var isSelected = isDaySelected(day, selectedDays, dateRange, isUtc);
    var isDisabled = isDayDisabled(day, disabledDays, shouldDisableDay);
    var isOutOfRange = isDayOutOfMonth(day, month);
    var isInRange = isDayInRange(day, dateRange) && !isDayOutOfMonth(day, month);
    var baseClasses = 'flex justify-center items-center rounded overflow-hidden w-8 h-8';
    var disabledClasses = isDisabled ? 'cursor-default text-silver-color' : '';
    var selectedClasses = isSelected ? 'bg-action-blue text-white' : '';
    var outOfMonthClasses = isOutOfRange ? 'text-medium-grey' : '';
    var inRangeClasses = isInRange ? 'bg-dark-fill-blue' : '';
    var hoverClasses = useMemo(function () {
      if (isSelected) return '';
      if (!isDisabled && !isInRange) return 'hover:bg-medium-fill-grey cursor-pointer';
      return 'cursor-none focus:bg-light-border-blue focus:outline-none';
    }, [isDisabled, isSelected]);
    return classNames(baseClasses, selectedClasses, disabledClasses, outOfMonthClasses, inRangeClasses, hoverClasses);
  };
  useEffect(function () {
    var handleKeyDown = function handleKeyDown(e) {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
      }
      var selectedDayIndex = days.flat().findIndex(function (day) {
        return isDaySelected(day, selectedDays, dateRange, isUtc);
      });
      if (selectedDayIndex === -1) return;
      var numRows = days.length;
      var numCols = days[0].length;
      var nextIndex;
      switch (e.key) {
        case 'ArrowUp':
          nextIndex = selectedDayIndex - numCols;
          break;
        case 'ArrowDown':
          nextIndex = selectedDayIndex + numCols;
          break;
        case 'ArrowLeft':
          nextIndex = selectedDayIndex - 1;
          break;
        case 'ArrowRight':
          nextIndex = selectedDayIndex + 1;
          break;
        default:
          return;
      }
      if (nextIndex >= 0 && nextIndex < numRows * numCols) {
        var nextDay = days[Math.floor(nextIndex / numCols)][nextIndex % numCols];
        if (!isDayDisabled(nextDay, disabledDays, shouldDisableDay)) {
          onDayClick(nextDay);
        }
      }
    };
    var calendarElement = calendarRef.current;
    if (calendarElement) {
      calendarElement.addEventListener('keydown', handleKeyDown);
    }
    return function () {
      if (calendarElement) {
        calendarElement.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [selectedDays, dateRange, isUtc, days, disabledDays, onDayClick, shouldDisableDay]);
  return /*#__PURE__*/React.createElement("div", {
    ref: calendarRef,
    className: calendarClass,
    id: id
  }, /*#__PURE__*/React.createElement("table", null, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, weekDays.map(function (day) {
    return /*#__PURE__*/React.createElement("th", {
      key: "week-day-".concat(day),
      className: dayClass
    }, day);
  }))), /*#__PURE__*/React.createElement("tbody", null, days.map(function (week) {
    return /*#__PURE__*/React.createElement("tr", {
      key: "week-".concat(week[0].week()),
      className: "h-full w-full leading-5 rounded"
    }, week.map(function (day) {
      return /*#__PURE__*/React.createElement("td", {
        key: "day-".concat(day.dayOfYear())
      }, /*#__PURE__*/React.createElement("div", {
        className: dayClass(day),
        role: "button",
        onClick: function onClick() {
          return isDayDisabled(day, disabledDays, shouldDisableDay) ? noop() : onDayClick(day);
        },
        tabIndex: isDayDisabled(day, disabledDays, shouldDisableDay) ? -1 : 0,
        onKeyDown: function onKeyDown(handleKeyDown) {
          return handleKeyDown.key === 'Enter' && onDayClick(day);
        }
      }, day.date()));
    }));
  }))));
};
UUCalendar.propTypes = {
  /** id for reference */
  id: PropTypes.string,
  /** className for reference */
  className: PropTypes.string,
  /** Override the inline-styles of the elements || **Not Recommended** */
  /** Current month to display */
  month: PropTypes.number.isRequired,
  /** Current year to display */
  year: PropTypes.number.isRequired,
  /** List of week days label */
  weekDays: PropTypes.array,
  /** Day click callback. Passes the moment Object */
  onDayClick: PropTypes.func,
  /** List of selected days. Can be a moment object or anything moment can parse */
  selectedDays: PropTypes.array,
  /** List of disabled days. Can be a moment object or anything moment can parse */
  disabledDays: PropTypes.array,
  /** Function returning a boolean */
  shouldDisableDay: PropTypes.func,
  /** Start and end date. This overrides the selectedDays prop */
  dateRange: PropTypes.shape({
    start: PropTypes.any,
    end: PropTypes.any
  }),
  /** are the dates in UTC format? */
  isUtc: PropTypes.bool
};
UUCalendar.defaultProps = {
  id: '',
  className: '',
  weekDays: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  onDayClick: noop,
  selectedDays: [],
  disabledDays: [],
  shouldDisableDay: noop,
  dateRange: {
    start: null,
    end: null
  },
  isUtc: true
};
export default UUCalendar;